/**
 * MobileBottomNav Component (Client Island)
 *
 * Diamond Architecture: Client Component for mobile navigation.
 * Fixed bottom tab bar, rendered from `manifests/routes.yaml`
 * `navigation.tabs[]` — the SAME table the native bars render from
 * (`navigation_kernel::nav_tabs()`), through the generated
 * `mobile-tabs_gen.ts` (webroutesgen): order, the shared `tab.*` label key,
 * the icon, and the `web:` twin's route builder. There is no hand list here.
 *
 * The ACTIVE tab is the generated `activeMobileTab(leafRouteId)`: the route
 * TREE decides (a route belongs to the tab whose twin's path is its nearest
 * path ancestor; the listings browse surfaces to the `web_browse` tab), never
 * a `pathname.startsWith` list.
 *
 * Hidden on desktop (md:hidden).
 */
import { useUnreadConversationsCount } from '@/shared/hooks/useUnreadConversationsCount';
import { activeMobileTab, MOBILE_TABS, type MobileTabIcon } from '@/shared/i18n/mobile-tabs_gen';
import { Link } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { formatBadgeCount } from '@letsgethome/lib/wasm';
import { plaintextString } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';
import { Heart, MessageSquare, Search, User } from 'lucide-react';
import { useMatches } from 'react-router';

/** The glyph for each icon of the closed vocabulary — exhaustive, no default. */
function iconFor(icon: MobileTabIcon): React.ElementType {
  switch (icon) {
    case 'search':
      return Search;
    case 'heart':
      return Heart;
    case 'message':
      return MessageSquare;
    case 'user':
      return User;
  }
}

export function MobileBottomNav() {
  const t = useTranslations();
  const tA11y = useTranslations('common');
  const unreadConversationsCount = useUnreadConversationsCount();

  // The Messages tab badge — the Rust SSOT `formatBadgeCount` (0 → undefined
  // → no badge, >99 → "99+"), fed by its own `/api/conversations/unread-count`
  // query (R5: decoupled from auth hydration).
  const messagesBadge = formatBadgeCount(unreadConversationsCount);

  const active = activeMobileTab(useMatches().at(-1)?.id);

  // The layer is `@letsgethome/ui/z-layers`' claim, not this file's:
  // this nav and a page's `StickyActionBar` are the two bands that can occupy
  // the bottom edge at once, and which one the reader meets is decided by two
  // integers stated there — never by DOM order, which is all the listing
  // page's contact bar ever had (LET-120). `no-raw-z-layer` bans a
  // hand-picked `z-*` here.
  //
  // The safe-area padding comes from the same seam because the class this file
  // used to carry, `pb-safe`, is not a Tailwind utility and is defined in no
  // `@utility` block, no config and no plugin: it emitted NOTHING for the
  // whole life of this component. `Z_LAYERS.bottomNav.safeArea` is an arbitrary
  // utility, so Tailwind mints it from the literal and it cannot be undefined.
  // (It still resolves to 0px until the document's viewport meta carries
  // `viewport-fit=cover` — see `z-layers`, where the viewport meta and the
  // insets are now welded to one another.)
  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 border-t border-border bg-background md:hidden',
        Z_LAYERS.bottomNav.safeArea,
        Z_LAYERS.bottomNav.className
      )}
      aria-label={tA11y('a11y.mobileNavigation')}
    >
      <div className="flex w-full">
        {MOBILE_TABS.map((tab) => {
          const Icon = iconFor(tab.icon);
          const isActive = tab.name === active;

          return (
            <Link
              key={tab.name}
              href={tab.href()}
              prefetch="intent"
              aria-current={isActive ? 'page' : undefined}
              data-tab={tab.name}
              className={cn(
                'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 transition-colors',
                isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <span className="relative">
                <Icon className={cn('h-5 w-5', isActive && 'fill-current')} />
                {tab.name === 'messages' && messagesBadge !== undefined && (
                  <span className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-none text-white">
                    {plaintextString(messagesBadge)}
                  </span>
                )}
              </span>
              <span className="text-[11px] leading-4">{t.raw(tab.labelKey)}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
